/**
 * GuardianContactCard — display card for the legacy guardian-contact slot.
 *
 * F.1c-iii Option B: shared card chrome (outer shell, header + Edit pencil,
 * badge row, field grid, status pill, empty state) is delegated to
 * `<FamilyMemberDisplayCard>`. Guardian-specific bits (`markedUnavailable`
 * → "Contact not available" body, the always-shown "Relationship" cell with
 * em-dash fallback, and the user-vs-check status icon rule) stay local.
 */

import { formatGuardianRelationshipLabel } from '@/ordering/canonicalOrder'
import {
  FamilyMemberDisplayCard,
  PrimaryGuardianBadge,
  type FieldSpec,
} from './FamilyMemberDisplayCard'
import { formatDobForDisplay } from './familyMemberDisplayHelpers'
import { CalendarMonth, Envelope, Phone, UserCircle } from 'flowbite-react-icons/outline'

export interface GuardianContactCardProps {
  contactNotAvailable?: boolean | null
  firstName?: string | null
  lastName?: string | null
  /** ISO YYYY-MM-DD */
  dateOfBirth?: string | null
  email?: string | null
  phone?: string | null
  relationshipToPatient?: string | null
  onEdit?: () => void
  /**
   * Read-only header link used when the guardian contact mirrors a
   * primary-guardian comparator: the link shows the relationship (e.g.
   * "Father") and navigates to that comparator's page. When provided, no Edit
   * pencil is rendered and the in-grid Relationship cell is suppressed (the
   * relationship is shown by the link instead).
   */
  relationshipLink?: { label: string; onClick: () => void }
  /** Match Biological Mother/Father attention styling when validation requires guardian. */
  needsAttention?: boolean
  /** Order In Progress: proband-only primary guardian (not biological parent card). */
  showPrimaryGuardianBadge?: boolean
  /** Bottom status; defaults to Optional, Complete when filled, or Not included for empty primary-guardian rows */
  statusMessage?: string
  statusVariant?: 'success' | 'info' | 'neutral'
}

export function GuardianContactCard({
  contactNotAvailable,
  firstName,
  lastName,
  dateOfBirth,
  email,
  phone,
  relationshipToPatient,
  onEdit,
  relationshipLink,
  needsAttention = false,
  showPrimaryGuardianBadge = false,
  statusMessage,
  statusVariant,
}: GuardianContactCardProps) {
  const name = [firstName, lastName].filter((p) => p && String(p).trim()).join(' ').trim()
  const emailTrim = email?.trim() ?? ''
  const phoneTrim = phone?.trim() ?? ''
  const dobTrim = dateOfBirth?.trim() ?? ''
  const relationshipLabel = formatGuardianRelationshipLabel(relationshipToPatient)
  const markedUnavailable = contactNotAvailable === true
  const hasContactFields = Boolean(name || dobTrim || emailTrim || phoneTrim || relationshipLabel)
  const hasData = markedUnavailable || hasContactFields
  const isComplete = hasData && !needsAttention

  const bottomStatusText =
    statusMessage ??
    (isComplete
      ? 'Complete'
      : showPrimaryGuardianBadge
        ? 'Not included'
        : 'Optional')
  const parentSetStatus = statusMessage != null || statusVariant != null || isComplete
  // Reserve green success strictly for actually-completed contact info. An
  // empty/optional guardian (not complete, not flagged, not a managed primary
  // guardian) is neutral gray — a green "Optional" pill wrongly implied the
  // section was done (UX-015).
  const bottomStatusVariant: 'success' | 'info' | 'neutral' =
    statusVariant ??
    (isComplete
      ? 'success'
      : needsAttention || showPrimaryGuardianBadge
        ? 'info'
        : 'neutral')
  const statusSuccessLead =
    bottomStatusVariant === 'success' && !parentSetStatus ? ('user' as const) : ('check' as const)

  const fields: FieldSpec[] | undefined = hasContactFields
    ? [
        { icon: Envelope, value: emailTrim, title: emailTrim || undefined },
        { icon: Phone, value: phoneTrim, title: phoneTrim || undefined },
        {
          icon: CalendarMonth,
          inlineLabel: 'DOB ',
          value: dobTrim ? formatDobForDisplay(dobTrim) : '',
        },
        // When the relationship is surfaced as a header link, drop the in-grid
        // Relationship cell to avoid showing it twice.
        ...(relationshipLink
          ? []
          : [
              {
                icon: UserCircle,
                inlineLabel: 'Relationship ',
                value: relationshipLabel,
                alwaysShow: true,
              } as FieldSpec,
            ]),
      ]
    : undefined

  return (
    <FamilyMemberDisplayCard
      title="Guardian Contact"
      onEdit={onEdit}
      headerLink={
        relationshipLink
          ? {
              // Read-only mirror of a primary-guardian comparator: the link
              // edits that comparator's contact info (routes to its Demographics
              // section), so it reads as an edit action rather than navigation.
              label: `Edit ${relationshipLink.label} Contact Info`,
              onClick: relationshipLink.onClick,
              ariaLabel: `Edit ${relationshipLink.label} contact info`,
            }
          : undefined
      }
      editAriaLabel="Edit guardian contact"
      needsAttention={needsAttention}
      badges={
        // The managed (read-only) variant mirrors a primary-guardian
        // comparator — the relationship link + "Uses {Person}'s contact" status
        // already make that clear, so the badge would be redundant.
        showPrimaryGuardianBadge && !relationshipLink ? (
          <PrimaryGuardianBadge>Primary guardian</PrimaryGuardianBadge>
        ) : null
      }
      nameLine={markedUnavailable ? null : name || null}
      fields={markedUnavailable ? undefined : fields}
      customBody={
        markedUnavailable ? (
          <span className="text-sm text-text-body dark:text-gray-400">Contact not available</span>
        ) : undefined
      }
      status={{
        text: bottomStatusText,
        variant: bottomStatusVariant,
        successLead: statusSuccessLead,
      }}
    />
  )
}
